import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "./css/Modal.css";

/**
 * Reusable modal component
 *
 * Props:
 * - open (bool): controls visibility
 * - onClose (fn): called when backdrop/Escape/close is triggered
 * - title (string | node): heading text; sets aria-labelledby for accessibility
 * - children (node): modal body content
 * - size ("sm" | "md" | "lg" | number): width behavior. number = % (e.g., 50)
 * - showClose (bool): show top-right close button (default true)
 * - closeOnBackdrop (bool): close when clicking outside (default true)
 * - ariaLabelledBy (string): custom id to label dialog; autogenerated if omitted
 * - initialFocusRef (ref): focus this element when modal opens
 */
export default function Modal({
  open,
  onClose,
  title = "",
  children,
  size = "md",
  showClose = true,
  closeOnBackdrop = true,
  ariaLabelledBy,
  initialFocusRef
}) {
  const dialogRef = useRef(null);
  const titleId = ariaLabelledBy || "app-modal-title";

  // ESC to close + scroll lock
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // initial focus
    const toFocus = initialFocusRef?.current || dialogRef.current?.querySelector("[data-autofocus]") || dialogRef.current;
    toFocus && toFocus.focus?.();

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, initialFocusRef]);

  if (!open) return null;

  // compute width class / inline
  const sizeClass =
    size === "sm" ? "modal--sm" :
    size === "lg" ? "modal--lg" :
    size === "md" ? "" : ""; // default handled below

  const inlineWidth = typeof size === "number" ? { width: `min(${size}%, 100%)` } : undefined;

  const handleBackdrop = () => {
    if (closeOnBackdrop) onClose?.();
  };

  const stop = (e) => e.stopPropagation();

  return createPortal(
    <div className="msn-modal-backdrop" onMouseDown={handleBackdrop}>
      <div
        className={`modal ${sizeClass}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        onMouseDown={stop}
        ref={dialogRef}
        style={inlineWidth}
        tabIndex={-1}
      >
        {showClose && (
          <button
            className="modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        )}

        {title ? <h3 id={titleId}>{title}</h3> : null}

        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
